/**
 * NativeRecorderDialog - Record a video with the device camera + mic using
 * MediaRecorder, preview, and publish to Comunidad Live.
 *
 * Flow:
 *   1. open camera (user chooses facingMode)
 *   2. REC → MediaRecorder records WebM/MP4 chunks
 *   3. STOP → stop recording, show preview with built-in <video controls>
 *   4. Title + descripción + sport → POST /community/live/native (multipart)
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  VideoCamera, Record, StopCircle, CameraRotate, X, PaperPlaneTilt, ArrowCounterClockwise,
} from "@phosphor-icons/react";

function fmt(seconds) {
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function NativeRecorderDialog({ open, onClose, onPublished }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | preview | uploading
  const [facing, setFacing] = useState("environment");
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sport, setSport] = useState("padel");

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const resetAll = () => {
    setPhase("idle");
    setElapsed(0);
    setBlob(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setTitle(""); setDescription(""); setSport("padel");
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  // Clean up on close
  useEffect(() => {
    if (!open) {
      stopStream();
      resetAll();
    }
    // eslint-disable-next-line
  }, [open]);

  const startCamera = async (desiredFacing = facing) => {
    try {
      stopStream();
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: desiredFacing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = s;
      setFacing(desiredFacing);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      toast.error("No se pudo abrir la cámara. Permite acceso en el navegador.");
    }
  };

  // Auto-start the camera when the dialog opens.
  useEffect(() => {
    if (open && phase === "idle" && !streamRef.current) {
      startCamera(facing);
    }
    // eslint-disable-next-line
  }, [open, phase]);

  const flipCamera = async () => {
    const next = facing === "user" ? "environment" : "user";
    await startCamera(next);
  };

  const startRec = () => {
    if (!streamRef.current) { toast.error("Cámara no disponible"); return; }
    // Prefer MP4 if the browser supports it, else WebM VP9/VP8.
    const candidates = ["video/mp4;codecs=avc1,mp4a", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    const mime = candidates.find((c) => window.MediaRecorder?.isTypeSupported?.(c)) || "";
    try {
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : {});
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "video/webm" });
        chunksRef.current = [];
        setBlob(b);
        const url = URL.createObjectURL(b);
        setPreviewUrl(url);
        setPhase("preview");
        stopStream();
      };
      rec.start(1000);
      recorderRef.current = rec;
      setElapsed(0);
      setPhase("recording");
      timerRef.current = setInterval(() => setElapsed((x) => x + 1), 1000);
    } catch (e) {
      toast.error("No se pudo iniciar la grabación: " + e.message);
    }
  };

  const stopRec = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const retake = () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setBlob(null);
    setElapsed(0);
    setPhase("idle");
    startCamera(facing);
  };

  const publish = async () => {
    if (!blob) { toast.error("No hay video grabado"); return; }
    if (!title.trim()) { toast.error("Agrega un título"); return; }
    setPhase("uploading");
    try {
      const fd = new FormData();
      const ext = (blob.type.includes("mp4") ? "mp4" : "webm");
      fd.append("file", blob, `clip.${ext}`);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("sport", sport);
      const r = await api.post("/community/live/native", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("¡Publicado en Comunidad Live!");
      onPublished?.(r.data?.live || null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo publicar");
      setPhase("preview");
    }
  };

  const sizeMB = blob ? (blob.size / 1024 / 1024).toFixed(1) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-rose-500/30 text-white max-w-xl" data-testid="native-rec-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase flex items-center gap-2">
            <VideoCamera size={22} weight="fill" className="text-rose-500" /> Graba un clip
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            Graba desde tu cámara, previsualiza y publica en Comunidad. Hasta 150MB (~2 min a 720p).
          </DialogDescription>
        </DialogHeader>

        <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
          {phase === "preview" ? (
            <video ref={videoRef} src={previewUrl} controls playsInline className="w-full h-full object-contain bg-black" data-testid="rec-preview" />
          ) : (
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" />
          )}

          {phase === "recording" && (
            <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500 text-white text-sm font-black uppercase tracking-widest shadow-lg">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC · {fmt(elapsed)}
            </div>
          )}
          {phase === "idle" && (
            <button
              onClick={flipCamera}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white border border-white/10 flex items-center justify-center hover:bg-black/80"
              aria-label="Voltear cámara"
              data-testid="rec-flip"
            >
              <CameraRotate size={18} />
            </button>
          )}
        </div>

        {phase === "preview" && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase text-white/70">Título</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Semifinal vs. equipo Satélite" className="bg-black/40 border-white/20" data-testid="rec-title" />
            </div>
            <div>
              <Label className="text-xs uppercase text-white/70">Descripción (opcional)</Label>
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/40 border-white/20" data-testid="rec-desc" placeholder="Punto final del partido..." />
            </div>
            <div>
              <Label className="text-xs uppercase text-white/70">Deporte</Label>
              <div className="grid grid-cols-5 gap-1 mt-1">
                {["padel", "tenis", "corredor", "balonpie", "general"].map((s) => (
                  <button key={s} onClick={() => setSport(s)} className={`px-2 py-1.5 rounded text-[11px] font-bold uppercase tracking-wide ${sport === s ? "bg-rose-500 text-white" : "bg-white/5 text-white/70 border border-white/10"}`} data-testid={`rec-sport-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-white/50">Tamaño: {sizeMB} MB · Duración: {fmt(elapsed)}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/5" data-testid="rec-cancel">
                <X size={16} className="mr-1" /> Cancelar
              </Button>
              <Button onClick={startRec} className="bg-rose-500 hover:bg-rose-600 text-white font-bold uppercase tracking-wide" data-testid="rec-start">
                <Record size={16} weight="fill" className="mr-1 animate-pulse" /> Grabar
              </Button>
            </>
          )}
          {phase === "recording" && (
            <>
              <Button variant="outline" onClick={flipCamera} className="border-white/20 text-white hover:bg-white/5" data-testid="rec-flip-live">
                <CameraRotate size={16} className="mr-1" /> Voltear
              </Button>
              <Button onClick={stopRec} className="bg-black text-rose-500 border border-rose-500 hover:bg-rose-500 hover:text-white font-bold uppercase tracking-wide" data-testid="rec-stop">
                <StopCircle size={16} className="mr-1" weight="fill" /> Detener
              </Button>
            </>
          )}
          {phase === "preview" && (
            <>
              <Button variant="outline" onClick={retake} className="border-white/20 text-white hover:bg-white/5" data-testid="rec-retake">
                <ArrowCounterClockwise size={16} className="mr-1" /> Repetir
              </Button>
              <Button onClick={publish} disabled={!title.trim()} className="bg-rose-500 hover:bg-rose-600 text-white font-bold uppercase tracking-wide disabled:opacity-50" data-testid="rec-publish">
                <PaperPlaneTilt size={16} className="mr-1" weight="fill" /> Publicar en Comunidad
              </Button>
            </>
          )}
          {phase === "uploading" && (
            <div className="w-full flex items-center justify-center gap-2 py-3">
              <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-white/70">Subiendo video...</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
